import { __awaiter } from 'tslib';
import 'long';
import '../proto/index.js';
import '../core/consts.js';
import { Logger } from '../core/metrics/logger.js';
import '../core/metrics/monitor.js';
import { ServiceIdentity } from '../core/service/RuntimeContext.js';
import '../core/network/NetworkItem.js';
import 'json-long';
import 'moize';
import '../core/model/Message.js';
import 'js-md5';
import '../core/utils/lz4.js';
import '../core/utils/serializer.js';
import '../adapter/manager/AdapterManager.js';
import '../adapter/manager/types.js';
import '../core/utils/ticker.js';
import '../adapter/abstract/IMNetApi.js';
import '../core/options.js';
import '../core/utils/uuid.js';
import '../core/service/ConversationManager.js';
import '../core/service/MessageManager.js';
import '@byted/uc-secure-sdk';
import '../core/plugin/DbProxy.js';
import { TrackerItem } from '../core/metrics/trackerTypes.js';
import { BasePlugin } from '../core/plugin/BasePlugin.js';
import '../core/model/CommandMessage.js';
import { StorageApiManager } from './api/StorageApiManager.js';
import { IndexedDbDriver } from './driver/idb.js';
import { DriverProxy } from './DriverProxy.js';
import { isIndexedDBSupport } from './envChecker.js';
import { StorageMonitorItem } from './metrics/StorageMetrics.js';

class StoragePlugin extends BasePlugin {
    install() {
        this.instance.clearCache = this.extendFunc(this.clearCache);
        // setup driver
        if (this.ctx.option.storage === true) {
            const driver = this.selectDriver();
            if (!driver) {
                Logger.ctxWarn(this.ctx, 'no support driver found, disable db integration');
                return;
            }
            this.driver = new DriverProxy(this.ctx, driver);
            this.register(ServiceIdentity.DbProxy, this.driver);
            // prepare default config
            // enable integrity check, ignore encryption/compression/retention
            if (this.ctx.option.integrityCheck === undefined) {
                this.ctx.option.integrityCheck = true;
            }
        }
        this.api = this.register(ServiceIdentity.StorageApi, StorageApiManager);
        this.register(ServiceIdentity.StoragePlugin, this);
    }
    /** @private */
    clearCache() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.dispose();
            return this.driver.clear();
        });
    }
    selectDriver() {
        if (this.ctx.option.driver !== undefined) { // using provided driver
            return this.ctx.option.driver;
        }
        else if (isIndexedDBSupport()) { // use indexeddb as driver
            return new IndexedDbDriver(this.ctx);
        }
        else if (typeof localStorage !== 'undefined') { // use localstorage as driver
            // TODO: not supported now
            return undefined;
        }
        // no db driver support
        return undefined;
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.driver && this.ctx.option.integrityCheck) {
                try {
                    yield this.checkConversation();
                }
                catch (err) {
                    Logger.ctxWarn(this.ctx, 'conversation integrity check err, be careful for conversation missing:', err);
                }
            }
        });
    }
    // integrity check
    checkConversation() {
        return __awaiter(this, void 0, void 0, function* () {
            const localCount = this.resolve(ServiceIdentity.ConversationManager).getConversationArray(c => !c.isOffline).length;
            const missing = [];
            for (const inbox of this.resolve(ServiceIdentity.InboxType).getInboxTypeArray()) {
                const cursor = this.driver.loadUserCursor(inbox);
                if (cursor === undefined) {
                    continue;
                }
                const resp = (yield this.api.GetConversationsCheckInfo({ inboxType: inbox })).conversation_checkinfo_list;
                resp.forEach(check => {
                    const local = this.resolve(ServiceIdentity.ConversationManager).getRaw(check.conversation_id);
                    // conversation missing or message missing
                    if (local === undefined || local.getMessageList().length === 0) {
                        missing.push(check);
                    }
                });
            }
            let repairMissingCount = 0;
            if (missing.length !== 0) {
                Logger.ctxWarn(this.ctx, 'conversation missing:', missing);
                // load missing conv
                const loadConv = missing.map(m => this.resolve(ServiceIdentity.ConversationManager).getWithCreateLocal(m.conversation_id, m.conversation_short_id.toString(), m.conversation_type));
                const updated = yield this.resolve(ServiceIdentity.ConversationManager).refreshAsync(loadConv);
                // load message
                for (const c of updated) {
                    try {
                        yield this.resolve(ServiceIdentity.CoreInstance).getMessagesByConversation({
                            conversation: c
                        });
                    }
                    catch (err) {
                        Logger.ctxError(this.ctx, 'pulling integrity check message error:', err);
                    }
                }
                repairMissingCount = loadConv.map(c => this.resolve(ServiceIdentity.ConversationManager).getRaw(c.id))
                    .map(c => c !== undefined && c.getMessageList().length !== 0)
                    .filter(b => !b).length;
            }
            this.resolve(ServiceIdentity.Monitor).emitTracker(TrackerItem.IntegrityCheckConversation, {
                is_pre_check: '1',
                leak_count: missing.length,
                link_mode: 0,
                bad_check: repairMissingCount === 0 ? 'false' : 'true'
            });
            this.resolve(ServiceIdentity.Monitor).emitCounter(StorageMonitorItem.IntegrityConversationCheckBefore, localCount);
            this.resolve(ServiceIdentity.Monitor).emitCounter(StorageMonitorItem.IntegrityConversationCheckFix, missing.length);
            yield this.resolve(ServiceIdentity.ConversationManager).refreshLocalAsync();
        });
    }
}

export { StoragePlugin };
//# sourceMappingURL=StoragePlugin.js.map
